import type { ActionPill } from "./chat"
import { CollectionRef } from "./collection"
import { DayObject, Timestamp } from "./date"
import { InspirationRef, PlaceRef } from "./entity"
import { MediaRef } from "./media"
import { AirportSummary, LocationSummary, PlacePhoto } from "./place"
import { PublicUser, UserRef } from "./user"

export interface InitTripProps {
  trip: Trip
}

export interface StopDetails {
  location: LocationSummary
  stop_type?: StopType
  days?: number
}

export interface Trip extends AbstractTrip {
  collaboration: boolean
  notes?: string
  destination?: LocationSummary

  major_stops?: TripStop[]
  major_stops_details?: StopDetails[]
  stops_area?: LocationSummary
  stops_area_name?: string

  budget?: Budget
  vibes?: TripVibe[]
  owner?: PublicUser // undefined if the owner is anonymous

  nezasa_reference?: string
  nezasa_title?: string
  package_language?: string
  package_country_code?: string
  package_currency?: string
  package_price?: number
  package_booked?: boolean
  package_car_rental?: boolean
  package_location_entity_ref?: PlaceRef

  originating_inspiration_ref?: InspirationRef
}

export type TripRef = `t-${string}`
const TripRefRE = /^t-[a-zA-Z0-9]{8}$/

// The strongest signal so far that a trip is real; replaces user_created.
export type TripEstablishedBy = "intent" | "trip_intent" | "itinerary" | "user"

export interface ThinTripSummary {
  ref: TripRef
  user_created?: boolean
  established_by?: TripEstablishedBy
  name?: string
  collaborative?: boolean
}

export interface TripSummary extends AbstractTrip {}

export interface TripStop {
  entity_ref: PlaceRef
  days: number | undefined
  stop_type?: StopType
}

interface AbstractTrip {
  id: number
  ref: TripRef
  name?: string
  owner_ref: UserRef
  user_refs: UserRef[] // owner and collaborators

  destination_name?: string // undefined if the trip has no destination
  destination_entity_ref?: PlaceRef
  time_zone: string

  user_created: boolean
  established_by?: TripEstablishedBy
  time_frame: TimeFrame

  draft_start_date: DayObject
  draft_duration: number

  travelers?: TravelerGroup | null
  rooms?: Room[]
  cover_photo?: PlacePhoto

  stops_area_name?: string
  stops_area_entity_ref?: PlaceRef

  roadtrip?: boolean

  calendar_trip_id?: number
  /** "calendar" for trips that came from Gmail or calendar. */
  source?: string
}

export interface TravelerGroup {
  adult_count: number
  child_count: number
  infant_count: number
  pet_count: number
}

// Combined cap on adults + children + infants across the booking flow.
// Per-room editors and the top-level travelers editor both read this so they
// stay consistent.
export const TOTAL_TRAVELERS_LIMIT = 16
export const PETS_LIMIT = 4

export interface TimeFrame {
  start_date?: DayObject
  duration?: number
  months?: number[] // ints in the range 1-12, sorted
}

export interface Room {
  adult_count: number
  child_ages: number[]
  pet_count: number
}

export interface TripVibe {
  title: string
  emoji?: string
}

export type Budget = "any" | "$" | "$$" | "$$$" | "$$$$"

export const BUDGET_TYPES: Budget[] = ["any", "$", "$$", "$$$", "$$$$"]

export interface TripCounts {
  chats: number
  scheduled_itinerary_events: number
  ideas: number
  bookings: number
  medias: number
  trip_details: number
}

export interface TripAdvice {
  id: number
  body: string
  time_sensitive: boolean
  action_pills: ActionPill[]
  trip_ref?: TripRef | null
  mindtrip_only?: boolean
}

export type NeedsAttentionSeverity = "high" | "medium" | "low"

export type TripDisruptionClass = "cancelled" | "diverted" | "delayed"

/** Resolved at read time, not stored: whoever the traveler has to reach for this row.
 *  `phone_number` is E.164 when dialable; `raw_phone` is the number as the source stored it. */
export interface NeedsAttentionContact {
  name?: string | null
  phone_number?: string | null
  raw_phone?: string | null
}

export interface NeedsAttentionFact {
  label: string
  value: string
}

export interface NeedsAttentionItem {
  id: number
  title: string
  body?: string | null
  severity: NeedsAttentionSeverity
  severity_label: string
  conflict_kind?: string | null
  facts: NeedsAttentionFact[]
  action_pills: ActionPill[]
  contact: NeedsAttentionContact
  /** When the window to act shuts. Past it the row still shows, in past tense, until
   *  expiry. Null means the row has no plan to miss. */
  actionable_until_timestamp?: Timestamp | null
  /** What the row says in place of its to-do once `actionable_until_timestamp` has passed. */
  missed_title: string
  /** What it offers then — empty for a plan that can only be reported missed. */
  missed_action_pills: ActionPill[]
  expires_at_timestamp?: Timestamp | null
  /** How a row from the trip's past list ended. Null on live rows. */
  outcome?: "done" | "expired" | null
  settled_by?: PublicUser | null
  settled_at_timestamp?: Timestamp | null
}

export interface TripDisruptionFlight {
  flight_iata?: string | null
  dep_iata?: string | null
  arr_iata?: string | null
}

/** One disrupted flight and everything it broke. The headline, summary and ordering are
 *  computed server-side — the client renders them, it does not derive them. */
export interface TripDisruption {
  key: string
  trip_ref: TripRef
  trip_name?: string | null
  cover_photo?: PlacePhoto | null
  disruption_class: TripDisruptionClass
  headline: string
  /** The triage agent's own read of what this costs the traveler. */
  summary: string
  /** A count of what the disruption broke, e.g. "3 flights and a hotel night affected". */
  affected_summary: string
  flight?: TripDisruptionFlight | null
  items: NeedsAttentionItem[]
}

export type TripAdviceDismissalReason =
  | "already_did"
  | "not_now"
  | "not_interested"
  | "other"

interface AbstractTripAdvicesResponse {
  trip_ref?: TripRef | null
}

export interface TripAdvicesReadyResponse extends AbstractTripAdvicesResponse {
  status: "ready"
  advices: TripAdvice[] // always at least one
}

export interface TripAdvicesPendingResponse extends AbstractTripAdvicesResponse {
  status: "pending"
}

export interface TripAdvicesNoContentResponse extends AbstractTripAdvicesResponse {
  status: "empty" | "stale" | "unavailable"
}

export type TripAdvicesResponse =
  | TripAdvicesReadyResponse
  | TripAdvicesPendingResponse
  | TripAdvicesNoContentResponse

export type ItineraryReviewStatus =
  | "unavailable"
  | "none"
  | "current"
  | "stale"
  | "reviewing"

export type ItineraryReviewCategory =
  | "timing_conflict"
  | "closed_place"
  | "geographic_backtracking"
  | "time_allocation"
  | "seasonal_mismatch"
  | "meal_time"
  | "duplicate"
  | "constraint_violation"

export interface ItineraryReviewFinding {
  category: ItineraryReviewCategory
  severity: "high" | "medium" | "low"
  description: string
  suggested_fix: string
  itinerary_event_id?: number | null
}

export interface ItineraryReviewResponse {
  status: ItineraryReviewStatus
  issues?: ItineraryReviewFinding[]
  issue_count?: number
}

export interface Collaborator {
  user: PublicUser
  first_name?: string
  last_name?: string
  active?: boolean
  owner?: boolean
}
export interface TripChat {
  chat_id: number
  visibility: "individual" | "group"
}
export interface TripInvitationPublicInfo {
  name?: string
  cover_photo_url?: string
}

export interface TripChange {
  trip_id?: number
  trip_changed?: boolean
  itinerary_changed?: boolean
  itinerary_event_ids?: number[]
  itinerary_comments_changed?: boolean
  trip_details_changed?: boolean
  deleted_itinerary_event_ids?: number[]
  folio_document_id?: number
}

// All attributes are optional -- omit (or use undefined) to leave
// an attribute unchanged, null to clear.
export interface TripUpdate {
  name?: string
  notes?: string
  time_frame?: TimeFrame
  travelers?: TravelerGroup | null
  rooms?: Room[]
  destination_entity_ref?: PlaceRef
  major_stops?: TripStop[]
  budget?: Budget
  vibes?: TripVibe[]
  stops_area_entity_ref?: PlaceRef
  roadtrip?: boolean
  user_created?: boolean
  package_car_rental?: boolean
  package_airport?: AirportSummary
  package_location_entity_ref?: PlaceRef
}

export interface TripTextExtraction {
  travelers?: TravelerGroup
  time_frame?: TimeFrame
  budget?: Budget
  is_road_trip?: boolean
  major_stops_details?: StopDetails[]
  location?: LocationSummary
  origin?: LocationSummary
}

export interface TripDetail {
  id: number
  creator_ref?: UserRef // undefined if the creator's account has been deleted
  text: string
  created_at: Timestamp
  updated_at: Timestamp
}

export type TripLogItem =
  | UserJoinedTripLogItem
  | UserLeftTripLogItem
  | ItineraryEventCreatedLogItem
  | ItineraryEventDeletedLogItem
  | MediaCreatedLogItem
  | MediaDeletedLogItem
  | ShareChatLogItem
  | ShareTripLogItem

interface AbstractTripLogItem {
  id: number
  timestamp: Timestamp
  user?: PublicUser // missing if user account has been deleted
}
interface UserJoinedTripLogItem extends AbstractTripLogItem {
  kind: "user_joined_trip"
}
interface UserLeftTripLogItem extends AbstractTripLogItem {
  kind: "user_left_trip"
}
export interface ItineraryEventCreatedLogItem extends AbstractTripLogItem {
  kind: "itinerary_event_created"
  itinerary_event_id: number
  itinerary_event_title: string
}
interface ItineraryEventDeletedLogItem extends AbstractTripLogItem {
  kind: "itinerary_event_deleted"
  itinerary_event_id: number
  itinerary_event_title: string
}

export interface MediaCreatedLogItem extends AbstractTripLogItem {
  kind: "media_created"
  media_ref: MediaRef
  media_type: string
  media_title: string
}

export interface MediaDeletedLogItem extends AbstractTripLogItem {
  kind: "media_deleted"
  media_ref: MediaRef
  media_type: string
  media_title: string
}

export interface ShareChatLogItem extends AbstractTripLogItem {
  kind: "share_chat"
}

export interface ShareTripLogItem extends AbstractTripLogItem {
  kind: "share_trip"
}

export type ShareKind = "copy" | "collaborate" | "entity"

export const TripTabItinerary = "itinerary"
export const TripTabIdeas = "ideas"
export const TripTabDetails = "details"
export const TripTabCalendar = "calendar"
export const TripTabBookings = "bookings"
export const TripTabMedia = "media"
export const TripTabChats = "chats"
export const TripTabDisruptions = "disruptions"

export type TripTab =
  | typeof TripTabItinerary
  | typeof TripTabIdeas
  | typeof TripTabDetails
  | typeof TripTabCalendar
  | typeof TripTabBookings
  | typeof TripTabMedia
  | typeof TripTabChats
  | typeof TripTabDisruptions

// tabs still available when trip features are limited for an org
export const LimitedTripTabs: readonly TripTab[] = [
  TripTabItinerary,
  TripTabDetails,
  TripTabChats,
]

export const TripTabs = [
  TripTabItinerary,
  TripTabIdeas,
  TripTabDetails,
  TripTabCalendar,
  TripTabBookings,
  TripTabMedia,
  TripTabChats,
  TripTabDisruptions,
]

export type StopType = "Departure" | "Stay" | "Visit" | "Transit" | "Return"

export type CommissionSourceRef = InspirationRef | CollectionRef

export function isTripRef(value: string): value is TripRef {
  return TripRefRE.test(value)
}
